import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MarketingShell from '../components/MarketingShell';
import { sb, SUPABASE_URL, SUPABASE_KEY } from '../lib/supabase';

/** Best-effort — the row is already saved before this runs, so a failure here never loses the lead. */
function notifyInvitationRequest(payload) {
  fetch(`${SUPABASE_URL}/functions/v1/notify-invitation-request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', apikey: SUPABASE_KEY },
    body: JSON.stringify(payload),
  }).catch(() => {});
}

const INITIAL_INVITE_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  company: '',
  platform: 'SAP S/4HANA',
};

export default function Signup() {
  const navigate = useNavigate();
  const location = useLocation();
  const [inviteForm, setInviteForm] = useState(INITIAL_INVITE_FORM);
  const [inviteStatus, setInviteStatus] = useState('idle');
  const [inviteError, setInviteError] = useState('');

  useEffect(() => {
    if (location.hash !== '#invite') return;
    const el = document.getElementById('invite');
    if (!el) return;
    const t = setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 50);
    return () => clearTimeout(t);
  }, [location.hash]);

  function updateInviteField(field) {
    return (e) => setInviteForm(prev => ({ ...prev, [field]: e.target.value }));
  }

  async function submitInvite(e) {
    e.preventDefault();
    setInviteStatus('submitting');
    setInviteError('');

    const payload = {
      first_name: inviteForm.firstName.trim(),
      last_name: inviteForm.lastName.trim(),
      email: inviteForm.email.trim(),
      company: inviteForm.company.trim(),
      platform_interest: inviteForm.platform,
    };

    const { error } = await sb.from('invitation_requests').insert(payload);

    if (error) {
      setInviteStatus('error');
      setInviteError(error.message || 'Something went wrong. Please try again or email hello@transformax.app.');
      return;
    }

    notifyInvitationRequest({
      firstName: payload.first_name,
      lastName: payload.last_name,
      email: payload.email,
      company: payload.company,
      platformInterest: payload.platform_interest,
    });

    setInviteStatus('success');
    setInviteForm(INITIAL_INVITE_FORM);
  }

  return (
    <MarketingShell>
      <section className="mk-hero">
        <div className="mk-copy">
          <div className="mk-eyebrow">Transformation programme management</div>
          <h1>Run your transformation programme on one platform — with an AI teammate that never starts from a blank slate</h1>
          <p>
            Transformax gives leadership, PMO teams, and delivery squads a single source of truth across governance, delivery, and finance — with Rex, an embedded AI assistant, drafting the next artefact from the last one so nobody starts from zero.
          </p>
          <div className="mk-hero-actions">
            <button className="btn-primary" onClick={() => { document.getElementById('invite')?.scrollIntoView({ behavior: 'smooth' }); }}>Request an invitation</button>
            <button className="btn-ghost" onClick={() => navigate('/auth?mode=signin&direct=1')}>Existing customer sign in</button>
          </div>
          <div className="mk-hero-note">By invitation during private beta · Works with SAP, Dynamics 365, Manhattan, Oracle Fusion, or any platform</div>
        </div>
        <div className="mk-box mk-pillar-mini-box">
          <h3>One platform, four pillars</h3>
          <div className="mk-pillar-mini"><span className="mk-dot" style={{ background: '#534AB7' }} /><div><b>Governance</b><span>Stage gates, RAID log, decisions</span></div></div>
          <div className="mk-pillar-mini"><span className="mk-dot" style={{ background: '#639922' }} /><div><b>Management</b><span>Plan, resourcing, status reports</span></div></div>
          <div className="mk-pillar-mini"><span className="mk-dot" style={{ background: '#BA7517' }} /><div><b>Workbench</b><span>Design, build, test, deploy</span></div></div>
          <div className="mk-pillar-mini"><span className="mk-dot" style={{ background: '#185FA5' }} /><div><b>Finance</b><span>Budget, forecast, benefits</span></div></div>
        </div>
      </section>

      <section className="mk-strip mk-persona-strip">
        <span className="mk-persona-label">Built for enterprise transformation teams</span>
        <div className="mk-persona-chips">
          <div className="mk-persona-chip">Enterprise CPOs and CFOs</div>
          <div className="mk-persona-chip">SI and consultancy programme directors</div>
          <div className="mk-persona-chip">Independent programme managers</div>
        </div>
      </section>

      <section className="mk-section mk-cta-band">
        <h2>See the full platform</h2>
        <p>Governance, management, workbench, and finance — walk through all four pillars and where Rex fits in.</p>
        <button className="btn-primary" onClick={() => navigate('/product')}>Explore the platform</button>
      </section>

      <section className="mk-invite" id="invite">
        <div className="mk-invite-copy">
          <div className="mk-eyebrow mk-eyebrow-dark">Private beta</div>
          <h2>Request an invitation</h2>
          <p>
            Transformax is onboarding a limited number of system integrators, consultancies, and enterprise programme teams as design partners ahead of general availability. Tell us about your programme and we&apos;ll be in touch.
          </p>
        </div>
        {inviteStatus === 'success' ? (
          <div className="mk-invite-form mk-invite-success">
            <h3>Request received</h3>
            <p>Thanks — we&apos;ll review your details and reply within a few business days.</p>
            <button type="button" className="btn-ghost" onClick={() => setInviteStatus('idle')}>Submit another request</button>
          </div>
        ) : (
          <form className="mk-invite-form" onSubmit={submitInvite}>
            <div className="row">
              <div>
                <label>First name</label>
                <input required value={inviteForm.firstName} onChange={updateInviteField('firstName')} placeholder="Jane" disabled={inviteStatus === 'submitting'} />
              </div>
              <div>
                <label>Last name</label>
                <input required value={inviteForm.lastName} onChange={updateInviteField('lastName')} placeholder="Doe" disabled={inviteStatus === 'submitting'} />
              </div>
            </div>
            <div className="row">
              <div className="full">
                <label>Work email</label>
                <input required type="email" value={inviteForm.email} onChange={updateInviteField('email')} placeholder="jane@company.com" disabled={inviteStatus === 'submitting'} />
              </div>
            </div>
            <div className="row">
              <div>
                <label>Company</label>
                <input required value={inviteForm.company} onChange={updateInviteField('company')} placeholder="Company name" disabled={inviteStatus === 'submitting'} />
              </div>
              <div>
                <label>Platform of interest</label>
                <select value={inviteForm.platform} onChange={updateInviteField('platform')} disabled={inviteStatus === 'submitting'}>
                  <option>SAP S/4HANA</option>
                  <option>Dynamics 365</option>
                  <option>Manhattan WMS</option>
                  <option>Oracle Fusion</option>
                  <option>Not sure yet</option>
                </select>
              </div>
            </div>
            <button type="submit" className="btn-primary" disabled={inviteStatus === 'submitting'}>
              {inviteStatus === 'submitting' ? 'Sending…' : 'Request invitation'}
            </button>
            {inviteStatus === 'error' && <div className="mk-invite-error">{inviteError}</div>}
            <div className="mk-invite-fine">We review every request and reply within a few business days.</div>
          </form>
        )}
      </section>
    </MarketingShell>
  );
}
